import {
    Box, Button, Dialog, DialogContent, IconButton, TextField, Typography
} from "@mui/material";
import {
    Close as CloseIcon,
    Business as BusinessIcon
} from "@mui/icons-material";
import * as Yup from 'yup';
import {useFormik} from "formik";
import {createCompany} from "../../services/system/CompanyService";
import {useCompany} from "../../contexts/CompanyContext";
import {useMessageContext} from "../../contexts/MessageContext";
import {getErrorMessage} from "../../enums/ErrorMessages";
import {RTL} from "../../cache/CacheRtl";
import {colors, borders} from "../ui/colors";

const inputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: borders.radius.md,
        backgroundColor: colors.white,
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.primary },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.primary, borderWidth: "2px" }
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.border }
};

const labelSx = { fontSize: "13px", fontWeight: 500, color: colors.textSecondary, marginBottom: "6px" };
const errorSx = { color: colors.error, fontSize: "12px", marginTop: "4px" };

const AddCompanyDialog = ({open, close, company, setReload}) => {
    const {notificationHandler} = useMessageContext();
    const {updateCompanies} = useCompany();

    const schema = Yup.object().shape({
        name: Yup.string().required('שדה חובה'),
        address: Yup.string().required('שדה חובה'),
    });

    const formik = useFormik({
        initialValues: {
            id: company ? company.id : null,
            name: company ? company.name : '',
            address: company ? company.address : '',
        },
        enableReinitialize: true,
        onSubmit: (values) => {
            createCompany(JSON.stringify({...values}))
                .then(() => notificationHandler.success('חברה עודכנה בהצלחה'))
                .then(() => setReload(prv => !prv))
                .then(handleClose)
                .then(updateCompanies)
                .catch(e => {
                    notificationHandler.error(getErrorMessage(e.response.data?.errorCode));
                });
        },
        validationSchema: schema
    });

    const handleClose = () => {
        formik.resetForm();
        close();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: borders.radius.lg,
                    minWidth: {xs: "90%", sm: "420px"},
                    maxWidth: "520px",
                    overflowY: "auto"
                }
            }}
        >
            <RTL>
                <Box dir="rtl">
                    {/* Header */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 20px",
                        backgroundColor: colors.bg,
                        borderBottom: borders.default
                    }}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <BusinessIcon sx={{color: colors.textSecondary, fontSize: "20px"}} />
                            <Typography sx={{ fontWeight: 600, fontSize: "16px", color: colors.textPrimary }}>
                                {company ? "עריכת חברה" : "הוספת חברה"}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleClose}
                            size="small"
                            sx={{ color: colors.textMuted, "&:hover": { backgroundColor: colors.bgHover, color: colors.textPrimary } }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <form onSubmit={formik.handleSubmit}>
                        <DialogContent sx={{padding: "24px 20px"}}>
                            <Box sx={{display: "flex", flexDirection: "column", gap: "16px"}}>
                                <Box>
                                    <Typography sx={labelSx}>*שם חברה</Typography>
                                    <TextField sx={inputSx} fullWidth size="small"
                                        onBlur={formik.handleBlur("name")} value={formik.values.name}
                                        onChange={formik.handleChange("name")} id="name" variant="outlined"
                                        placeholder="הכנס שם החברה" />
                                    {formik.errors.name && formik.touched.name && <Typography sx={errorSx}>{formik.errors.name}</Typography>}
                                </Box>
                                <Box>
                                    <Typography sx={labelSx}>*כתובת</Typography>
                                    <TextField sx={inputSx} fullWidth size="small" multiline rows={3}
                                        onBlur={formik.handleBlur("address")} value={formik.values.address}
                                        onChange={formik.handleChange("address")} id="address" variant="outlined"
                                        placeholder="הכנס כתובת החברה" />
                                    {formik.errors.address && formik.touched.address && <Typography sx={errorSx}>{formik.errors.address}</Typography>}
                                </Box>
                            </Box>
                        </DialogContent>

                        {/* Footer */}
                        <Box sx={{
                            display: "flex", justifyContent: "flex-start", gap: 1.5,
                            padding: "16px 20px", borderTop: borders.default, backgroundColor: colors.bg
                        }}>
                            <Button disabled={!formik.dirty || !formik.isValid} type="submit" variant="contained"
                                sx={{
                                    backgroundColor: colors.primary, color: colors.white, fontSize: "13px", fontWeight: 600,
                                    padding: "8px 24px", borderRadius: borders.radius.md, textTransform: "none",
                                    "&:hover": { backgroundColor: colors.primaryDark },
                                    "&.Mui-disabled": { backgroundColor: colors.border, color: colors.textMuted }
                                }}>
                                שמור
                            </Button>
                            <Button onClick={handleClose} variant="outlined"
                                sx={{
                                    borderColor: colors.border, color: colors.textSecondary, fontSize: "13px", fontWeight: 500,
                                    padding: "8px 20px", borderRadius: borders.radius.md, textTransform: "none",
                                    "&:hover": { borderColor: colors.textSecondary, backgroundColor: colors.bgHover }
                                }}>
                                ביטול
                            </Button>
                        </Box>
                    </form>
                </Box>
            </RTL>
        </Dialog>
    );
};

export default AddCompanyDialog;
